// グループ集計（AT-STYLE様。docs/33_グループ集計_AT-STYLE_設計たたき台_20260709.md）
//
// グループ（例: 株式会社AT-STYLE）に所属するドライバーのCSV配達個数を、グループ専用の単価
// （マスタ branch で自動判定）で集計し、グループ宛の通知書1枚分の売上itemsを作る。
// メンバーの個人通知書（マスタ単価）は通常どおり全員分作成される。グループ宛通知書はそれに
// 追加で1枚作られるもので、個別除外はしない（このファイルは items 生成のみ担当）。
//
// ■ 単価分岐（実測突合済み。docs/33 §3）
//   - ヤマト系メンバー（branch が「佐川」で始まらない）: branch ∈ {周南, 光} なら yamatoShunanHikari、
//     それ以外は yamatoOther。宅急便・EAZY / ネコポス・クロネコDM便 の2行（個数0でも行を出す）。
//   - 佐川周南メンバー: 既存の周南特殊計上ロジック（sagawaShunan.ts の便種分類）を再利用。
//     「佐川急便」= 宅急便系件数（001/006/007/009/061の実績件数）、
//     「佐川急便 対面以外」= 商業貨物(008)の実績件数
//                          ＋ 商業貨物複数口（商業貨物の実績個数 − 実績件数）
//                          ＋ 複数口（宅急便系の実績個数 − 実績件数）   ※本城さん原文の3成分
//   - それ以外の佐川メンバー: 「佐川急便」1行（メール便以外の実績個数。2026-07-13 本城さん指摘で件数→個数に訂正）。
//   - マスタ未登録メンバーは原則スキップ（Settings UI で警告）。ただし佐川CSVに担当者名一致が
//     あればマスタ無しでも佐川(周南以外)扱いで行を出す（中川紘人の実例）。

import type { Driver, GroupRule, ItemRow, SagawaBenkindRule, SagawaRow, YamatoRow } from "../../types";
import { normalizeName } from "../normalize";
import { classifySagawaCode } from "./sagawaRules";
import { classifyShunanBenkind } from "./sagawaShunan";

// ヤマトの周南・光判定（マスタ branch がこのいずれかなら yamatoShunanHikari 単価）
const YAMATO_SHUNAN_HIKARI_BRANCHES = ["周南", "光"];

function sumYamatoQty(nameNormalized: string, rows: YamatoRow[]): { takkyu: number; nekopos: number } {
  let takkyu = 0;
  let nekopos = 0;
  for (const r of rows) {
    if (r.driverNameNormalized !== nameNormalized) continue;
    takkyu += r.takkyubinQty;
    nekopos += r.nekoposQty;
  }
  return { takkyu, nekopos };
}

// 非周南の佐川：メール便(nekopos)・対象外(ignore)以外の実績個数を合算（「メール便以外の実績個数」）。
// ※ 当初は実績件数(countItems)で実装していたが、本城さん指摘（2026-07-13）により実績個数(countQty)が正と確定。
//   通常の佐川個別通知書と同じ「個数」ベース（件数ベースなのは周南のみ）。
function sumSagawaTakkyuKosu(nameNormalized: string, rows: SagawaRow[], rules: SagawaBenkindRule[]): number {
  let n = 0;
  for (const r of rows) {
    if (r.driverNameNormalized !== nameNormalized) continue;
    const cat = classifySagawaCode(r.benkindCode, rules);
    if (cat === "nekopos" || cat === "ignore" || cat == null) continue;
    n += r.countQty;
  }
  return n;
}

// 周南の佐川：宅急便系の実績件数（＝「佐川急便」数量）と「対面以外」数量。
// 対面以外 = 商業貨物の実績件数
//          + 商業貨物複数口（= max(0, 商業貨物の実績個数 − 実績件数)）
//          + 複数口（= max(0, 宅急便系の実績個数 − 実績件数)）
// ※「個数 − 件数」の方向は既存 sagawaShunan.ts（吉岡里紗4月で検証済み）と同じ。
function sumSagawaShunan(
  nameNormalized: string,
  rows: SagawaRow[],
  rules: SagawaBenkindRule[]
): { takkyuKensu: number; taimenIgai: number } {
  let takkyuKensu = 0;
  let takkyuQty = 0;
  let shogyoKensu = 0;
  let shogyoQty = 0;
  for (const r of rows) {
    if (r.driverNameNormalized !== nameNormalized) continue;
    const cat = classifyShunanBenkind(r.benkindCode, rules);
    if (cat === "takkyu") {
      takkyuKensu += r.countItems;
      takkyuQty += r.countQty;
    } else if (cat === "shogyo") {
      shogyoKensu += r.countItems;
      shogyoQty += r.countQty;
    }
  }
  const shogyoFukusu = Math.max(0, shogyoQty - shogyoKensu); // 商業貨物複数口
  const fukusuguchi = Math.max(0, takkyuQty - takkyuKensu); // 複数口
  return { takkyuKensu, taimenIgai: shogyoKensu + shogyoFukusu + fukusuguchi };
}

function mkItem(
  deliveryDate: string,
  name: string,
  unitPrice: number,
  quantity: number,
  detail: string,
  carrier: "yamato" | "sagawa"
): ItemRow {
  return {
    deliveryDate,
    name,
    unitPrice,
    quantity,
    amount: unitPrice * quantity,
    taxRate: "10%",
    detail,
    category: "sales",
    carrier,
  };
}

// グループ1件分の売上itemsを生成（メンバー×区分で1行ずつ。数量0でも行を出す）。
// メンバーが1人もCSV/マスタに見つからなければ空配列（＝グループ通知書自体を作らない）を返す。
export function buildGroupItems(
  rule: GroupRule,
  drivers: Driver[],
  yamatoRows: YamatoRow[],
  sagawaRows: SagawaRow[],
  benkindRules: SagawaBenkindRule[],
  monthLastDay: string
): ItemRow[] {
  const items: ItemRow[] = [];
  for (const rawName of rule.memberNames) {
    const name = rawName.trim();
    if (!name) continue;
    const n = normalizeName(name);
    const driver = drivers.find((d) => d.nameNormalized === n);

    if (driver && driver.branch.startsWith("佐川")) {
      if (driver.branch === "佐川周南") {
        const { takkyuKensu, taimenIgai } = sumSagawaShunan(n, sagawaRows, benkindRules);
        items.push(mkItem(monthLastDay, "佐川急便", rule.sagawaTakkyu, takkyuKensu, `${name}　佐川急便`, "sagawa"));
        items.push(
          mkItem(monthLastDay, "佐川急便 対面以外", rule.sagawaTaimenIgai, taimenIgai, `${name}　佐川急便 対面以外`, "sagawa")
        );
      } else {
        const qty = sumSagawaTakkyuKosu(n, sagawaRows, benkindRules);
        items.push(mkItem(monthLastDay, "佐川急便", rule.sagawaTakkyu, qty, `${name}　佐川急便`, "sagawa"));
      }
      continue;
    }

    if (driver) {
      // ヤマト系（佐川以外の branch）
      const q = sumYamatoQty(n, yamatoRows);
      const unit = YAMATO_SHUNAN_HIKARI_BRANCHES.includes(driver.branch) ? rule.yamatoShunanHikari : rule.yamatoOther;
      items.push(mkItem(monthLastDay, "宅急便・EAZY", unit.takkyu, q.takkyu, `${name}　宅急便・EAZY`, "yamato"));
      items.push(
        mkItem(monthLastDay, "ネコポス・クロネコDM便", unit.nekopos, q.nekopos, `${name}　ネコポス・クロネコDM便`, "yamato")
      );
      continue;
    }

    // マスタ未登録：佐川CSVに担当者名一致があれば佐川(周南以外)扱いで行を出す（中川紘人の実例）。
    // それ以外（ヤマトのみ一致 or どこにも一致しない）はスキップ＝ Settings 画面の警告で拾う。
    const inSagawa = sagawaRows.some((r) => r.driverNameNormalized === n);
    if (inSagawa) {
      const qty = sumSagawaTakkyuKosu(n, sagawaRows, benkindRules);
      items.push(mkItem(monthLastDay, "佐川急便", rule.sagawaTakkyu, qty, `${name}　佐川急便`, "sagawa"));
    }
  }
  return items;
}

// メンバーを名簿から外してよいかの判断材料（本城さん・徳永さん 2026-08-26 の突合で判明した落とし穴）。
//
// グループ集計は在籍/退職を見ないため、**契約終了日を含む月はまだ実績があり、メンバーに残っていれば
// 正しく計上される**。逆に早く外すとその月の分がグループ宛通知書から消えて支払額が減る。
// 設定画面はこの値を使い、実績がある人を外そうとしたときに確認を出す（docs/36 §8）。
export type MemberCsvUsage = {
  /** 取込済みCSVに1件でも実績があるか */
  found: boolean;
  yamatoTakkyu: number;
  yamatoNekopos: number;
  /** 佐川の実績個数（便種は問わない。警告用の目安） */
  sagawaQty: number;
};

export function memberCsvUsage(name: string, yamatoRows: YamatoRow[], sagawaRows: SagawaRow[]): MemberCsvUsage {
  const n = normalizeName(name.trim());
  let yamatoTakkyu = 0;
  let yamatoNekopos = 0;
  let sagawaQty = 0;
  let found = false;
  for (const r of yamatoRows) {
    if (r.driverNameNormalized !== n) continue;
    found = true;
    yamatoTakkyu += r.takkyubinQty;
    yamatoNekopos += r.nekoposQty;
  }
  for (const r of sagawaRows) {
    if (r.driverNameNormalized !== n) continue;
    found = true;
    sagawaQty += r.countQty;
  }
  return { found, yamatoTakkyu, yamatoNekopos, sagawaQty };
}

// グループ計上の見える化（docs/33。ICB様問い合わせ「◯◯さんが取り込まれていない」対策）。
// rule.memberNames のうち、実際に取込済みCSV（ヤマト・佐川）に1件でも登場する名前だけを返す。
// ＝ 各ドライバーの個人通知書とは別に、グループ通知書にも計上される対象。
// マスタ登録の有無の判定は行わない（呼び出し側の責務）。
export function groupAggregatedMembers(rule: GroupRule, yamatoRows: YamatoRow[], sagawaRows: SagawaRow[]): string[] {
  const result: string[] = [];
  for (const rawName of rule.memberNames) {
    const name = rawName.trim();
    if (!name) continue;
    const n = normalizeName(name);
    const inCsv =
      yamatoRows.some((r) => r.driverNameNormalized === n) || sagawaRows.some((r) => r.driverNameNormalized === n);
    if (inCsv) result.push(name);
  }
  return result;
}

// ===== 初期設定（本城さん 7/7〜7/9 確定情報。AT-STYLE様5月分正解PDFの28名） =====

let seq = 0;
const rid = () => `grp-${++seq}`;

export const DEFAULT_GROUP_RULES: GroupRule[] = [
  {
    id: rid(),
    groupName: "株式会社AT-STYLE",
    memberNames: [
      "山本光一",
      "草場雪子",
      "内山剛",
      "兼重優輔",
      "竹本右京",
      "有安健",
      "沖津勇次",
      "大住泰毅",
      "上田知美",
      "山本忍龍",
      "種田由紀恵",
      "髙橋英一",
      "天野克紀",
      // 中川紘人様は 2026年5月で契約解除・今後の集計不要（本城さん確認 7/9）→ プリセット対象外
      "綿谷啓美",
      "河津友樹",
      "池田裕幸",
      "渡辺真一",
      "横山潤耶",
      "大野雅明",
      "西原浩一",
      "岩宮皓晴",
      "末長満",
      "宮原陽貴",
      "岩宮耀貴",
      "須﨑哲也",
      "寺澤英則",
      "岡永崇",
    ],
    yamatoShunanHikari: { takkyu: 15, nekopos: 10 },
    yamatoOther: { takkyu: 20, nekopos: 10 },
    sagawaTakkyu: 15,
    sagawaTaimenIgai: 5,
  },
];
